import { StatusBadge } from '@chiroup/components/StatusBadge';
import { SeverityEnum } from '@chiroup/components/enums/severity';
import { Button } from '@chiroup/components/ui/button';
import { ColumnDef, DataTable, DataTableColumnHeader } from '@chiroup/components/ui/data-table';
import {
  BILLING_READINESS_WINDOWS,
  BillingReadinessRow,
  BillingReadinessSeverity,
  BillingReadinessWindow,
  DEFAULT_BILLING_READINESS_WINDOW,
} from '@chiroup/core/types/BillingReadiness.type';
import React, { useMemo, useState } from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import useBillingReadiness from '../../hooks/useBillingReadiness';
import LoadingPage from '../common/LoadingPage';
import Header from '../layout/Header';
import BillingReadinessDetailModal from './BillingReadinessDetailModal';

const SEVERITY_META: Record<BillingReadinessSeverity, { severity: SeverityEnum; label: string }> = {
  [BillingReadinessSeverity.Critical]: { severity: SeverityEnum.Error, label: 'Critical' },
  [BillingReadinessSeverity.Moderate]: { severity: SeverityEnum.Warning, label: 'Moderate' },
  [BillingReadinessSeverity.Normal]: { severity: SeverityEnum.Success, label: 'Normal' },
  [BillingReadinessSeverity.None]: { severity: SeverityEnum.Note, label: '—' },
};

/** Numeric rank so columns sort by urgency (Critical first) regardless of label. */
const severityRank: Record<BillingReadinessSeverity, number> = {
  [BillingReadinessSeverity.Critical]: 3,
  [BillingReadinessSeverity.Moderate]: 2,
  [BillingReadinessSeverity.Normal]: 1,
  [BillingReadinessSeverity.None]: 0,
};

const SignalBadge: React.FC<{ severity: BillingReadinessSeverity; text: string }> = ({
  severity,
  text,
}) => {
  const meta = SEVERITY_META[severity];
  return <StatusBadge severity={meta.severity} status={text} />;
};

/** A ratio cell: color-coded percentage pill + the raw count, kept subtle. */
const RatioCell: React.FC<{
  severity: BillingReadinessSeverity;
  pct: number;
  numerator: number;
  denominator: number;
}> = ({ severity, pct, numerator, denominator }) => (
  <div className="flex items-center justify-end gap-2">
    <SignalBadge severity={severity} text={`${pct}%`} />
    <span className="text-xs text-gray-400 dark:text-dark-gray-500">
      {numerator}/{denominator}
    </span>
  </div>
);

const WINDOW_LABELS: Record<BillingReadinessWindow, string> = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  60: 'Last 60 days',
  90: 'Last 90 days',
};

const columns: ColumnDef<BillingReadinessRow>[] = [
  {
    id: 'clinic',
    // Combine name + id so the toolbar search matches both.
    accessorFn: row => `${row.clinicName} #${row.clinicId}`,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Clinic" />,
    cell: ({ row }) => (
      <div className="font-medium text-gray-900 dark:text-white">
        {row.original.clinicName}
        <span className="ml-2 text-xs text-gray-400">#{row.original.clinicId}</span>
      </div>
    ),
  },
  {
    // Aggregate risk (critical*100 + moderate; each row has at most 5 signals so
    // there's no collision). The API already returns rows most-critical-first;
    // this column lets a user re-apply that ordering after sorting another column.
    id: 'risk',
    accessorFn: row => row.criticalCount * 100 + row.moderateCount,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Risk" />,
    cell: ({ row }) => {
      const { criticalCount, moderateCount } = row.original;
      if (!criticalCount && !moderateCount) {
        return <span className="text-xs text-gray-400">—</span>;
      }
      return (
        <div className="flex items-center gap-2 text-xs font-medium">
          {criticalCount > 0 && (
            <span className="text-red-600 dark:text-red-400">{criticalCount} critical</span>
          )}
          {moderateCount > 0 && (
            <span className="text-amber-600 dark:text-amber-500">{moderateCount} moderate</span>
          )}
        </div>
      );
    },
  },
  {
    id: 'transmission',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Claim transmission" />,
    accessorFn: row => severityRank[row.transmission.severity],
    cell: ({ row }) => {
      const { businessDaysSince, severity } = row.original.transmission;
      // Critical + no date = an expected clinic that has never transmitted.
      const text =
        businessDaysSince == null
          ? severity === BillingReadinessSeverity.Critical
            ? 'No claims'
            : 'No history'
          : `${businessDaysSince} business days`;
      return <SignalBadge severity={severity} text={text} />;
    },
  },
  {
    id: 'rejection',
    meta: { align: 'right' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Rejection ratio" />,
    accessorFn: row => severityRank[row.rejection.severity],
    cell: ({ row }) => {
      const { available, numerator, denominator, pct, severity } = row.original.rejection;
      if (!available) return <span className="text-xs text-gray-400">Not available</span>;
      if (pct == null) return <span className="text-xs text-gray-400">No data</span>;
      return (
        <RatioCell severity={severity} pct={pct} numerator={numerator} denominator={denominator} />
      );
    },
  },
  {
    id: 'denial',
    meta: { align: 'right' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Denial ratio" />,
    accessorFn: row => severityRank[row.denial.severity],
    cell: ({ row }) => {
      const { numerator, denominator, pct, severity } = row.original.denial;
      if (pct == null) return <span className="text-xs text-gray-400">No ERA data</span>;
      return (
        <RatioCell severity={severity} pct={pct} numerator={numerator} denominator={denominator} />
      );
    },
  },
  {
    id: 'lastPayment',
    meta: { align: 'right' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last posted payment" />,
    accessorFn: row => row.lastPayment.daysSince ?? Infinity,
    cell: ({ row }) => {
      const { daysSince, severity } = row.original.lastPayment;
      if (daysSince == null) {
        return <span className="text-xs text-gray-400">No payment history</span>;
      }
      return <SignalBadge severity={severity} text={`${daysSince} days ago`} />;
    },
  },
  {
    id: 'lastEra',
    meta: { align: 'right' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last ERA received" />,
    accessorFn: row => row.lastEra.daysSince ?? Infinity,
    cell: ({ row }) => {
      const { daysSince, severity } = row.original.lastEra;
      if (daysSince == null) return <span className="text-xs text-gray-400">No ERA history</span>;
      return <SignalBadge severity={severity} text={`${daysSince} days ago`} />;
    },
  },
  {
    id: 'clearinghouse',
    accessorFn: row => row.clearinghouse ?? '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Clearinghouse" />,
    cell: ({ row }) =>
      row.original.clearinghouse ? (
        <span className="text-sm text-gray-700 dark:text-dark-gray-300">
          {row.original.clearinghouse}
        </span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
  },
  {
    // SF onboarding enrichment. equalsString so the faceted filter matches the
    // exact stage (e.g. "Go-Live" doesn't also match "Pre Go-Live").
    id: 'phase',
    accessorFn: row => row.phase ?? '',
    filterFn: 'equalsString',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Onboarding phase" />,
    cell: ({ row }) =>
      row.original.phase ? (
        <span className="text-sm text-gray-700 dark:text-dark-gray-300">{row.original.phase}</span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
  },
  {
    id: 'onboarder',
    accessorFn: row => row.onboarder ?? '',
    filterFn: 'equalsString',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Onboarder" />,
    cell: ({ row }) =>
      row.original.onboarder ? (
        <span className="text-sm text-gray-700 dark:text-dark-gray-300">
          {row.original.onboarder}
        </span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
  },
];

const BillingReadinessReport: React.FC = () => {
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('analytics.view');
  const [reportWindow, setReportWindow] = useState<BillingReadinessWindow>(
    DEFAULT_BILLING_READINESS_WINDOW
  );
  // Anomaly view (off by default): non-EDI clinics that still have activity.
  const [unexpectedActivity, setUnexpectedActivity] = useState(false);
  const [detail, setDetail] = useState<{ id: number; name: string } | null>(null);
  const { data, isFetching } = useBillingReadiness(reportWindow, canView, unexpectedActivity);

  // Filter options derived from the loaded rows (phase/onboarder come from SF).
  const phaseOptions = useMemo(() => {
    const set = new Set<string>();
    (data?.rows ?? []).forEach(r => r.phase && set.add(r.phase));
    return Array.from(set)
      .sort()
      .map(v => ({ label: v, value: v }));
  }, [data]);
  const onboarderOptions = useMemo(() => {
    const set = new Set<string>();
    (data?.rows ?? []).forEach(r => r.onboarder && set.add(r.onboarder));
    return Array.from(set)
      .sort()
      .map(v => ({ label: v, value: v }));
  }, [data]);

  if (permissionsLoading) {
    return <LoadingPage />;
  }

  if (!canView) {
    return (
      <>
        <Header title="Billing Readiness Report" />
        <div className="p-6 text-sm text-gray-500 dark:text-dark-gray-400">
          You do not have permission to view the billing readiness report.
        </div>
      </>
    );
  }

  const toolbarRight = (
    <div className="flex items-center gap-2">
      <Button
        variant={unexpectedActivity ? 'default' : 'ghost'}
        size="sm"
        onClick={() => setUnexpectedActivity(v => !v)}
        title="Show clinics with no live clearinghouse integration that still have recorded activity"
      >
        Unexpected activity
      </Button>
      <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-dark-gray-600 dark:bg-dark-gray-800">
        {BILLING_READINESS_WINDOWS.map(value => (
          <Button
            key={value}
            variant={reportWindow === value ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setReportWindow(value)}
          >
            {WINDOW_LABELS[value]}
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Header title="Billing Readiness Report" />
      <div className="p-6">
        <p className="mb-3 text-xs text-gray-500 dark:text-dark-gray-400">
          {unexpectedActivity
            ? 'Anomaly view: clinics with no live clearinghouse integration that nonetheless have recorded billing activity — a likely misconfiguration or a disabled integration still receiving files. Figures reflect activity recorded since this report went live.'
            : "Lists clinics configured to bill electronically (an active, production clearinghouse integration). A clinic that should be transmitting and hasn't sent claims is flagged Critical. Clinics without a live clearinghouse integration are intentionally excluded — if one you expect is missing, check its integration setup. Figures reflect billing activity recorded since this report went live; earlier history isn't included."}
        </p>
        <DataTable
          columns={columns}
          data={data?.rows ?? []}
          loading={!data && isFetching}
          fetching={!!data && isFetching}
          pagination={{ pageSize: 25 }}
          onRowClick={row => setDetail({ id: row.clinicId, name: row.clinicName })}
          toolbar={{
            search: { column: 'clinic', placeholder: 'Search by clinic name or ID...' },
            filters: [
              { column: 'phase', title: 'Onboarding phase', options: phaseOptions },
              { column: 'onboarder', title: 'Onboarder', options: onboarderOptions },
            ],
            rightSlot: toolbarRight,
          }}
        />
      </div>
      {detail && (
        <BillingReadinessDetailModal
          clinicId={detail.id}
          clinicName={detail.name}
          reportWindow={reportWindow}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
};

export default BillingReadinessReport;
